import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMe } from '@/api/me';
import { TAMANHO_PAGINA_LISTA, useListaPrecosPaginada } from '@/api/lista-precos';
import { usePoliticaPreco } from '@/api/liberacao';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { BotaoExportar, ErroExportacao, useExportar } from '@/components/exportar';
import { formatarContagem } from '@/lib/format';
import { BotaoRelatorios } from '@/relatorios';
import { SeloEstimada } from '@/components/reforma/reforma-ui';
import { useSessao } from '@/lib/session';
import { FiltrosLista } from './filtros-lista';
import { GradePrecos } from './grade-precos';
import { HistoricoPraticados, type AlvoHistorico } from './historico-praticados';
import { juntarPaginas, paraConsulta, paraRelatorio, temFiltro, type BuscaLista } from './tipos';

/**
 * Tela "Lista de preços" (/precificar/lista), SO LEITURA e exportação. Uma linha por item x tabela x condição, calculada pela API
 * (so a pagina visivel). Para alterar um preço, a Formação de preço (link "Abrir na Formação" em cada linha).
 * Nenhum preco, lucro ou faixa e calculado aqui: sugerido, lucro e sinalizadores vem de GET /precificacao/lista.
 */
export function ListaPrecosPage({ busca }: { busca: BuscaLista }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const me = useMe();
  // Politica NEGOCIADO: sem praticado, rascunho nem lucro simulado; o orcamento parte do sugerido.
  const negociado = usePoliticaPreco().data?.politicaPreco === 'NEGOCIADO';
  // Sem custo.ver a API omite custo e "sem ficha" (e recusa o filtro semFicha): a coluna e o filtro saem da tela.
  const comCusto = me.data?.permissoes.includes('custo.ver') ?? false;
  // Sem lucro.ver a API omite lucro, meta e MC (o representante ve so a cor da faixa).
  const comLucro = me.data?.permissoes.includes('lucro.ver') ?? false;
  // Com praticado.gravar a lista traz o preco do rascunho (nao publicado) de cada linha, so para consulta.
  const podeGravar = me.data?.permissoes.includes('praticado.gravar') ?? false;
  const ordem = busca.ordem ?? 'codigo';
  const consultaBusca = comCusto || !busca.semFicha ? busca : { ...busca, semFicha: undefined };

  const consulta = useListaPrecosPaginada({ ...paraConsulta(consultaBusca), ...(podeGravar ? { rascunho: true } : {}) });
  const [historico, setHistorico] = useState<AlvoHistorico | null>(null);
  const { exportando, erro: erroExportar, exportar } = useExportar();

  const paginas = useMemo(() => consulta.data?.pages ?? [], [consulta.data]);
  const linhas = useMemo(() => juntarPaginas(paginas), [paginas]);
  const referenciaEm = paginas[0]?.referenciaEm;
  const tabelasIgnoradas = paginas.flatMap((p) => p.tabelasIgnoradas);
  // Legenda da reforma: etapa da data de referencia (vem da API) e selo quando alguma aliquota e estimada.
  const reformaLegenda = useMemo(() => {
    const reformas = linhas.flatMap((l) => (l.tipo === 'calculada' && l.linha.reforma ? [l.linha.reforma] : []));
    const primeira = reformas[0];
    return primeira ? { etapa: primeira.etapa, estimada: reformas.some((r) => r.aliquotaEstimada) } : null;
  }, [linhas]);

  const mudar = useCallback(
    (mudanca: Partial<Record<keyof BuscaLista, BuscaLista[keyof BuscaLista] | undefined>>) => {
      void navigate({ to: '/precificar/lista', search: (anterior: BuscaLista) => ({ ...anterior, ...mudanca }) as BuscaLista, replace: true });
    },
    [navigate],
  );

  const carregarMais = useCallback(() => {
    if (consulta.hasNextPage && !consulta.isFetchingNextPage && !consulta.isFetching) void consulta.fetchNextPage();
  }, [consulta]);
  const podeCarregarMais = Boolean(consulta.hasNextPage) && !consulta.isFetchingNextPage && !consulta.isFetching;

  // Pagina curta (filtros apertados): a grade nao rola, entao pede a proxima sozinha.
  useEffect(() => {
    if (podeCarregarMais && linhas.length < 12) carregarMais();
  }, [podeCarregarMais, linhas.length, carregarMais]);

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver a lista de preços.</Mensagem>;
  else if (consulta.isError && !consulta.data) {
    corpo = (
      <div className="p-4">
        <ErroFormacao erro={consulta.error} onTentar={() => void consulta.refetch()} />
      </div>
    );
  } else if (!consulta.data) corpo = <Mensagem papel="status">Carregando a lista de preços…</Mensagem>;
  else if (linhas.length === 0) {
    corpo = temFiltro(busca) ? (
      <Mensagem papel="status">
        Nenhum item com esses filtros.{' '}
        <button
          type="button"
          className="font-medium text-primary hover:underline"
          onClick={() => void navigate({ to: '/precificar/lista', search: (b: BuscaLista) => ({ ...b, q: undefined, tipo: undefined, abaixo: undefined, semPraticado: undefined, semFicha: undefined }) as BuscaLista, replace: true })}
        >
          Limpar filtros
        </button>
      </Mensagem>
    ) : (
      <Mensagem papel="status">
        Nenhum item ativo para listar nesta data. Cadastre itens em Cadastros › Produtos e serviços e publique preços na Formação de preço.
      </Mensagem>
    );
  } else {
    corpo = (
      <GradePrecos
        linhas={linhas}
        comCusto={comCusto}
        comLucro={comLucro}
        ordem={ordem}
        podeCarregarMais={podeCarregarMais}
        onOrdem={(o) => mudar({ ordem: o === 'codigo' ? undefined : o })}
        onCarregarMais={carregarMais}
        onHistorico={(l) =>
          setHistorico({ itemId: l.itemId, tabelaId: l.tabelaId, condicaoId: l.condicaoId, codigo: l.codigo, descricao: l.descricao, tabelaCodigo: l.tabelaCodigo })
        }
        negociado={negociado}
      />
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Precificar <span aria-hidden="true">/</span> Lista de preços
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Lista de preços</h1>
          <p className="mt-1 text-ink-secondary">Todos os produtos nas tabelas, com o praticado ao lado do sugerido.</p>
          <p role="note" className="mt-2 text-small text-ink-secondary">
            A lista é só para consulta e exportação. Para alterar um preço, use a Formação de preço.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <BotaoExportar rotulo="Exportar Excel" exportando={exportando} desabilitado={empresaId === null} onClick={() => void exportar('/precificacao/lista.xlsx', paraConsulta(consultaBusca))} />
          <BotaoExportar rotulo="Exportar CSV" exportando={exportando} desabilitado={empresaId === null} onClick={() => void exportar('/precificacao/lista.csv', paraConsulta(consultaBusca))} />
          <BotaoRelatorios tela="lista-precos" params={paraRelatorio(busca, Boolean(consultaBusca.semFicha))} />
        </div>
      </div>

      <ErroExportacao erro={erroExportar} />
      {tabelasIgnoradas.length > 0 && (
        <div role="status" data-testid="tabelas-ignoradas" className="rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-3 text-small text-warning-ink">
          {tabelasIgnoradas.map((t) => (
            <div key={t.tabelaId}>
              Tabela {t.codigo} fora da lista: {t.message} ({t.code})
            </div>
          ))}
        </div>
      )}

      <section aria-label="Lista de preços" aria-busy={consulta.isFetching} className="overflow-hidden rounded-2xl border border-border bg-card">
        <FiltrosLista busca={busca} onBusca={mudar} comSemFicha={comCusto} />
        {corpo}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-small text-ink-secondary">
          <span data-testid="contagem-lista" aria-live="polite">
            {consulta.data
              ? `Mostrando ${formatarContagem(linhas.length)} ${linhas.length === 1 ? 'linha' : 'linhas'}${consulta.hasNextPage ? ` (carregadas de ${TAMANHO_PAGINA_LISTA} em ${TAMANHO_PAGINA_LISTA}; há mais)` : ''}`
              : ' '}
            {referenciaEm ? ` · referência ${referenciaEm.split('-').reverse().join('/')} · preços com IPI e custo financeiro da condição` : ''}
          </span>
          <span className="flex items-center gap-3">
            {consulta.hasNextPage && (
              <button
                type="button"
                onClick={carregarMais}
                disabled={!podeCarregarMais}
                className="h-8 rounded-lg border border-border bg-card px-3 text-small hover:bg-line-soft disabled:opacity-50"
              >
                {consulta.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </button>
            )}
            <span>
              <span className="font-semibold text-success-ink">verde</span> = praticado acima do sugerido
            </span>
            {reformaLegenda && (
              <span data-testid="legenda-reforma" className="flex items-center gap-2">
                Reforma: {reformaLegenda.etapa}
                {reformaLegenda.estimada && <SeloEstimada />}
              </span>
            )}
          </span>
        </div>
      </section>

      {negociado && (
        <p role="note" className="text-small text-ink-muted">
          Política de preço: negociado — o orçamento parte do sugerido.
        </p>
      )}
      <HistoricoPraticados alvo={historico} onFechar={() => setHistorico(null)} />
    </div>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
